<script setup>
// Import functions
// Import components
import OmniTableTopSlotPaginated from 'components/OmniTableTopSlotPaginated.vue'
import OmniOverviewCards from 'components/UI/OmniOverviewCards.vue'
import OmniPageHeader from 'components/UI/OmniPageHeader.vue'
import { useOverviewAutoGrow } from 'src/composables/useOverviewInclude'
import { usePageLoading } from 'src/composables/usePageLoading'
import { getServiceTitle } from 'src/utils/servicePresentation'

// Constants declarations
const { t, te } = useI18n({ useScope: 'global' })
const attrs = useAttrs()
const slots = useSlots()

// Component props
const props = defineProps({
  showOverview: { type: Boolean, default: false },
  hideSummaryCards: { type: Boolean, default: false },
  overviewName: { type: String, default: null },
  updatedAt: { type: [Number, String, Date], default: null },
  addItem: {
    type: Function,
    required: false,
    default: null,
  },
  addItemLabel: {
    type: [null, String],
    required: false,
    default: null,
  },
  refreshItemList: {
    type: Function,
    required: false,
    default: null,
  },
  itemsService: {
    type: String,
    required: true,
    default: null,
  },
  columns: {
    type: Array,
    required: false,
    default: null,
  },
  rows: {
    type: Array,
    required: true,
    default: () => {
      return []
    },
  },
  pagination: {
    type: Object,
    required: false,
    default: () => {
      return {
        rowsPerPage: 25,
      }
    },
  },
  rowsNumber: {
    type: Number,
    required: true,
  },
  menuItems: {
    type: Array,
    required: false,
    default: () => {
      return []
    },
  },
  heading: {
    type: String,
    required: false,
    default: null,
  },
  headingTag: {
    type: String,
    required: false,
    default: null,
  },
  removeTopPadding: {
    type: Boolean,
    required: false,
    default: false,
  },
  excludeFromFilter: {
    type: Array,
    required: false,
    default: () => {
      return []
    },
  },
  translateFilterOptions: {
    type: Boolean,
    required: false,
    default: true,
  },
  searchable: {
    type: Boolean,
    required: false,
    default: true,
  },
  showButtons: {
    type: Boolean,
    required: false,
    default: true,
  },
  hideHeader: {
    type: Boolean,
    required: false,
    default: false,
  },
  noTopSlot: {
    type: Boolean,
    required: false,
    default: false,
  },
  hideActions: {
    type: Boolean,
    required: false,
    default: false,
  },
  showNoDataOverlay: {
    type: Boolean,
    required: false,
    default: false,
  },
  additionalButtons: {
    type: Array,
    required: false,
    default: () => {
      return []
    },
  },
  showAllOptionInPaginationOptions: {
    type: Boolean,
    required: false,
    default: true,
  },
  visibleColumns: {
    type: Array,
    required: false,
    default: null,
  },
  hidePagination: {
    type: Boolean,
    required: false,
    default: false,
  },
  loading: {
    type: Boolean,
    required: false,
    default: false,
  },
  autoGrow: {
    type: Boolean,
    required: false,
    default: false,
  },
  addRowIndex: {
    type: Boolean,
    required: false,
    default: true,
  },
  rowKey: {
    type: [String, Function],
    required: false,
    default: null,
  },
})

// const emit = defineEmits(['update:searchValue', 'update:filterToggle'])
const tableSearchModel = defineModel('search', { type: String, required: false })
const tableFilterToggleModel = defineModel('filterToggle', {
  type: Object,
  required: false,
  default: null,
})

// Store(s)

const autoGrowEnabled = useOverviewAutoGrow(() => props.autoGrow)

// Refs
const omniTable = ref(null)
const omniTableKey = ref(0)
const tableHasNoData = ref(false)
const docHeight = ref(window.document.body.offsetHeight - 60)

// Computed properties
const pageTitle = computed(() => props.heading || getServiceTitle(props.overviewName || props.itemsService, t, te))

const rowsWithIndex = computed(() => {
  if (!props.addRowIndex) {
    return props.rows
  }

  let dataWithIndex = []
  for (const [index, value] of props.rows.entries()) {
    if (value.rowIndex !== index) {
      value.rowIndex = index
    }
    dataWithIndex.push(value)
  }
  return dataWithIndex
})
const tableFilter = computed(() => {
  return {
    search: tableSearchModel.value,
    menuItems: props.menuItems,
  }
})
const usesPageLoading = usePageLoading(() => props.loading)
const tableAttrs = computed(() => {
  const attrsClone = {
    ...attrs,
  }

  if (tableHasNoData.value) {
    if (attrsClone.style) {
      delete attrsClone.style
    }
  }
  if (rowsWithIndex.value.length < 1) {
    if (!attrsClone.hideBottom && props.loading) {
      attrsClone.hideBottom = true
    }

    delete attrsClone.class
  }
  if (process.env.FRONTEND_TYPE === 'Customer') {
    attrsClone.hideBottom = attrsClone.hideBottom ?? attrsClone['hide-bottom'] ?? true
  }
  return attrsClone
})

const filterSlotNames = computed(() => Object.keys(slots).filter((name) => name.startsWith('filter-')))
const tableSlotNames = computed(() => Object.keys(slots).filter((name) => name !== 'top-slot-custom' && !name.startsWith('filter-')))

const setDocHeight = () => {
  if (!autoGrowEnabled.value) return
  const tableBounds = omniTable.value?.$el?.getBoundingClientRect()
  if (process.env.FRONTEND_TYPE === 'Admin' && !tableBounds?.height) return
  const page = omniTable.value?.$el?.closest('.omni-portal-page')
  const bottomPadding = page ? Number.parseFloat(window.getComputedStyle(page).paddingBottom) : 24
  docHeight.value =
    process.env.FRONTEND_TYPE === 'Admin'
      ? Math.max(320, window.innerHeight - (tableBounds?.top ?? 60) - bottomPadding)
      : window.document.body.offsetHeight - 60
}
// Static variables

// Methods

// Watchers
onBeforeMount(() => {
  window.addEventListener('resize', setDocHeight)
})

onUnmounted(() => {
  window.removeEventListener('resize', setDocHeight)
})

// Lifecycle hooks
onUpdated(() => {
  setDocHeight()
})
</script>

<template>
  <div class="relative-position flex-grow-1">
    <q-resize-observer v-if="$frontendType === 'Admin' && autoGrowEnabled" @resize="setDocHeight" />
    <omni-page-header v-if="$frontendType === 'Admin' && showOverview && !noTopSlot" :updated-at="updatedAt" :refresh="refreshItemList" :loading="loading" />
    <OmniOverviewCards
      v-if="$frontendType === 'Admin' && showOverview && !noTopSlot && !hideSummaryCards"
      :overview="overviewName || itemsService"
      :label="pageTitle"
      :updated-at="updatedAt"
    />
    <q-table
      ref="omniTable"
      flat
      class="omni-table"
      :class="{ 'omni-data-table-vscroll': autoGrowEnabled && rowsWithIndex.length > 0 }"
      :key="omniTableKey"
      :rows="rowsWithIndex"
      :rows-per-page-options="[1, 5, 10, 25, 50, 100, 250, 500]"
      :rows-per-page-label="$t('Shared.Globals.Rows per page')"
      :pagination-label="(start, end, total) => `${start}-${end} ${t('Shared.Globals.Of').toLowerCase()} ${total}`"
      :columns="columns ? columns : undefined"
      :row-key="rowKey || ((row) => row.rowIndex)"
      :pagination="pagination"
      :loading="loading"
      :no-data-label="t('Shared.Globals.No data')"
      :no-results-label="rowsWithIndex.length > 0 ? t('Shared.Globals.No results') : t('Shared.Globals.No data')"
      :visible-columns="visibleColumns ? visibleColumns : undefined"
      :hide-pagination="hidePagination"
      :style="autoGrowEnabled && rowsWithIndex.length > 0 ? 'min-height: ' + docHeight + 'px; max-height: ' + docHeight + 'px;' : undefined"
      v-bind="tableAttrs"
    >
      <template #top v-if="!noTopSlot">
        <omni-table-top-slot-paginated
          :additional-buttons="additionalButtons"
          :searchable="searchable"
          :translate-filter-options="translateFilterOptions"
          v-model:search="tableSearchModel"
          :items-service="itemsService"
          :filters="showButtons ? tableFilter : undefined"
          :heading="props.heading"
          :heading-tag="props.headingTag"
          :remove-top-padding="props.removeTopPadding"
          v-model:filter-toggle="tableFilterToggleModel"
          :add-item="!hideHeader ? props.addItem : undefined"
          :add-item-label="props.addItemLabel"
          :refresh-item-list="$frontendType === 'Admin' && showOverview ? undefined : props.refreshItemList"
        >
          <template v-for="name in filterSlotNames" #[name]="slotData">
            <slot :name="name" v-bind="slotData"></slot>
          </template>
          <template v-if="slots['top-slot-custom']" #top-slot-custom>
            <slot name="top-slot-custom"></slot>
          </template>
        </omni-table-top-slot-paginated>
      </template>
      <template #loading>
        <q-inner-loading v-if="!usesPageLoading" showing color="primary" />
      </template>
      <template v-for="name in tableSlotNames" #[name]="slotData">
        <slot :name="name" v-bind="slotData || {}"></slot>
      </template>
    </q-table>
  </div>
</template>

<style scoped></style>
